/**
 * True when an event targets a TEXT-editing element (text field or contenteditable), whose keystrokes
 * and pastes produce text that belongs to it rather than to page-level listeners.
 *
 * A `<select>` is deliberately excluded: it consumes no text, but it keeps focus after the user picks
 * an option, so treating it as editable left every physical keypress swallowed (no readout / depress /
 * drill) until a page refresh moved focus off it. The keyboard-exploration listeners must keep working
 * while a dropdown is the focused element.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("input, textarea, [contenteditable]") != null;
}

/**
 * True when a keypress would be eaten by a focused `<select>`'s type-ahead: a printable character
 * (letter, digit, symbol) with no command modifier held.
 *
 * The other half of the story above. A `<select>` keeps focus after a pick, and its native
 * type-ahead jumps to the first option starting with the typed character - so pressing the Build key
 * over the still-focused command-card dropdown silently changed the SELECTED UNIT instead of opening
 * the build submenu. Callers default-prevent these, exactly as the readout already does for the
 * scroll and focus-stealing keys, so the keypress belongs to the board. Ctrl/Meta/Alt combinations
 * are left alone: those are browser shortcuts, not type-ahead.
 */
export function isSelectTypeahead(e: KeyboardEvent): boolean {
  if (!(e.target instanceof HTMLSelectElement)) return false;
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}
